import React from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import styles from './Button.module.scss';
import Icon from '../Icon';

export type AllButtonType = 'button' | 'submit' | 'reset';

export enum ButtonType {
  PRIMARY_LIGHT = 'primary-light',
  PRIMARY_DARK = 'primary-dark',
  SECONDARY_LIGHT = 'secondary-light',
  SECONDARY_DARK = 'secondary-dark',
  TEXT_LIGHT = 'text-light',
  TEXT_DARK = 'text-dark',
}

export interface ButtonProps {
  type?: ButtonType;
  btnType?: AllButtonType;
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  ariaLabel?: string;
  iconName?: string,
  iconPosition?: 'right' | 'left' | null,
  iconSize?: number,
  href?: string,
  targetBlank?: boolean,
  fitContent?: boolean,
  size?: 'default' | 'small',
}

const Button: React.FC<ButtonProps> = ({
  type = ButtonType.PRIMARY_LIGHT,
  btnType = 'button',
  children,
  onClick,
  disabled = false,
  loading = false,
  className = '',
  ariaLabel = '',
  iconName,
  iconPosition = 'right',
  iconSize,
  href,
  targetBlank,
  fitContent = false,
  size = 'default',
}) => {
  const isDarkMode = type.includes('-dark');
  const baseType = type.replace(/-light|-dark$/, '');

  const buttonClasses = clsx(
    styles.button,
    styles[size],
    styles[baseType],
    fitContent && styles.fitContent,
    loading && styles.loading,
    !children && styles.round,
    isDarkMode && styles.dark,
    className,
    iconPosition === 'left' && styles.leftIcon,
  );

  const content = (
    <>
      {children && children}
      {iconName && (
        <Icon
          name={iconName}
          style={{ fontSize: iconSize }}
        />
      )}
    </>
  );

  return (href ? (
    <Link
      href={href}
      aria-label={ariaLabel}
      target={targetBlank ? '_blank' : undefined}
      className={buttonClasses}
    >
      {content}
    </Link>
  ) : (
    <button
      // eslint-disable-next-line react/button-has-type
      type={btnType || 'button'}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  ));
};

export default Button;
